import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { GoogleButton } from "@/components/GoogleButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/32846096/pexels-photo-32846096.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }} />
        <div className="absolute inset-0 bg-slate-900/70" />
        <Link to="/" className="relative font-display text-2xl font-bold tracking-tight">Rimpatrio<span className="text-blue-400">24</span></Link>
        <div className="relative">
          <h2 className="font-display text-4xl font-bold leading-tight">{t("hero.title")}</h2>
          <p className="mt-4 text-slate-300 max-w-md">{t("hero.subtitle")}</p>
        </div>
        <div className="relative text-sm text-slate-400">© Rimpatrio24 — rimpatrio24.it</div>
      </div>

      <div className="flex flex-col">
        <div className="flex justify-end p-4"><LanguageSwitcher /></div>
        <div className="flex-1 flex items-center justify-center px-6 pb-16">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <h1 className="font-display text-3xl font-bold text-slate-900">{t("auth.login")}</h1>
            <p className="text-slate-500 mt-2 text-sm">Rimpatrio24 — un unico account per tutto.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-1.5">
                <Label>{t("auth.email")}</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("auth.password")}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-blue-900 hover:bg-blue-800 h-11">
                {loading ? "..." : t("auth.login")}
              </Button>
            </form>
            <div className="flex items-center gap-3 my-5">
              <div className="h-px bg-slate-200 flex-1" />
              <span className="text-xs uppercase tracking-wider text-slate-400">{t("auth.orDivider")}</span>
              <div className="h-px bg-slate-200 flex-1" />
            </div>
            <GoogleButton />
            <p className="mt-6 text-sm text-slate-500">
              {t("nav.register")}? <Link to="/register" className="text-blue-900 font-medium hover:underline">{t("auth.register")}</Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
